import { propagation, ROOT_CONTEXT, trace, type Context } from "@opentelemetry/api";
import { ConsoleInstrumentation } from "@opentelemetry/browser-instrumentation/experimental/console";
import { ErrorsInstrumentation } from "@opentelemetry/browser-instrumentation/experimental/errors";
import { FetchInstrumentation } from "@opentelemetry/browser-instrumentation/experimental/fetch";
import { NavigationInstrumentation } from "@opentelemetry/browser-instrumentation/experimental/navigation";
import { NavigationTimingInstrumentation } from "@opentelemetry/browser-instrumentation/experimental/navigation-timing";
import { ResourceTimingInstrumentation } from "@opentelemetry/browser-instrumentation/experimental/resource-timing";
import { UserActionInstrumentation } from "@opentelemetry/browser-instrumentation/experimental/user-action";
import { WebVitalsInstrumentation } from "@opentelemetry/browser-instrumentation/experimental/web-vitals";
import { startBrowserSdk } from "@opentelemetry/browser-sdk";
import { browserDetector } from "@opentelemetry/browser-sdk/browser-detector";
import {
  createDefaultSessionIdGenerator,
  createLocalStorageSessionStore,
  createSessionManager,
} from "@opentelemetry/browser-sdk/session";
import { ZoneContextManager } from "@opentelemetry/context-zone";
import { detectResources, resourceFromAttributes } from "@opentelemetry/resources";
import { UAParser } from "ua-parser-js";

if (import.meta.env.VITE_OTEL_SERVICE_NAME) {
  const ua = new UAParser();

  let resource = resourceFromAttributes({
    "browser.name": ua.getBrowser().name,
    "browser.version": ua.getBrowser().version,
    "device.type": ua.getDevice().type,
    "device.vendor": ua.getDevice().vendor,
    "device.model": ua.getDevice().model,
    "cpu.architecture": ua.getCPU().architecture,
    "os.name": ua.getOS().name,
    "os.version": ua.getOS().version,
    "engine.name": ua.getEngine().name,
    "engine.version": ua.getEngine().version,
  });
  resource = resource.merge(detectResources({ detectors: [browserDetector] }));

  const ignoreUrls = [
    /\/\.opentelemetry\//u, // Don't continually re-emit logs for emitting logs
    /\/(src|node_modules|assets|generated|@vite|@react-refresh)\//u, // Localhost loading of page assets
    /\/\.(js|css|html|svg|ftl|ttf)$/u, // Static resources
    /^https:\/\/maps\.googleapis\.com/u, // External
  ];

  const sessionManager = createSessionManager({
    sessionIdGenerator: createDefaultSessionIdGenerator(),
    sessionStore: createLocalStorageSessionStore(),
    maxDuration: 4 * 60 * 60,
    inactivityTimeout: 30 * 60,
  });
  await sessionManager.start();

  class CustomZoneContextManager extends ZoneContextManager {
    private rootContextOverride: Context = ROOT_CONTEXT;

    setRootContext(context: Context) {
      this.rootContextOverride = context;
    }

    override active() {
      const base = super.active();
      if (base === ROOT_CONTEXT) return this.rootContextOverride;
      return base;
    }
  }

  const contextManager = new CustomZoneContextManager();
  const sdk = startBrowserSdk({
    logLevel: "INFO",
    resourceAttributes: resource.attributes,
    serviceName: import.meta.env.VITE_OTEL_SERVICE_NAME,
    logs: { exportConfig: { url: `${import.meta.env.VITE_SERVER_URL}/.opentelemetry/v1/logs` } },
    traces: {
      exportConfig: { url: `${import.meta.env.VITE_SERVER_URL}/.opentelemetry/v1/traces` },
      contextManager,
    },
    instrumentations: [
      new ErrorsInstrumentation(),
      new NavigationInstrumentation(),
      new NavigationTimingInstrumentation(),
      new ResourceTimingInstrumentation({ ignoreUrls }),
      new UserActionInstrumentation(),
      new WebVitalsInstrumentation(),
      new ConsoleInstrumentation(),
      new FetchInstrumentation({ ignoreUrls }),
    ],
  });
  if (sdk.invalidConfig) {
    throw new TypeError("Invalid OpenTelemetry SDK");
  }

  contextManager.setRootContext(
    propagation.extract(ROOT_CONTEXT, {
      traceparent: document.head.querySelector<HTMLMetaElement>("meta[name=traceparent]")?.content,
    }),
  );
}

export function getTracer() {
  return trace.getTracerProvider().getTracer("bale");
}
